import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, patch, del } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { FormDialog } from "@/components/common/FormDialog";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/common/EmptyState";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { Plus, ChevronLeft, ChevronRight, Trash2, Video, Copy } from "lucide-react";
import { toast } from "sonner";

const MOD_COLOR = { pm: "bg-blue-500", crm: "bg-violet-500", hrms: "bg-amber-500", company: "bg-emerald-500" };
const TYPE_STYLE = {
  leave: "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300",
  holiday: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300",
  deadline: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300",
  company: "bg-violet-100 text-violet-800 dark:bg-violet-500/20 dark:text-violet-300",
};
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const LEGEND = [
  { label: "PM", cls: "bg-blue-500" }, { label: "Sales", cls: "bg-violet-500" }, { label: "HR", cls: "bg-amber-500" },
  { label: "Leave", cls: "bg-amber-400" }, { label: "Holiday", cls: "bg-emerald-500" }, { label: "Deadline", cls: "bg-red-500" },
];

export default function CalendarPage() {
  const qc = useQueryClient();
  const { can } = useApp();
  const [cursor, setCursor] = useState(() => { const n = new Date(); return { year: n.getFullYear(), month: n.getMonth() }; });
  const [detailEvent, setDetailEvent] = useState(null);
  const { data: events, isLoading } = useQuery({ queryKey: ["calendar"], queryFn: () => get("/calendar") });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const create = useMutation({
    mutationFn: (b) => post("/calendar", b),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["calendar"] });
      if (res.google_sync?.synced) toast.success(res.google_sync.meet_link ? "Meeting scheduled — Google Calendar invite + Meet link sent" : "Meeting scheduled — Google Calendar invite sent");
      else if (res.google_sync?.error === "not_connected") toast.success("Meeting scheduled (connect your Google account to auto-sync future meetings)");
      else toast.success("Meeting scheduled");
    },
  });
  const remove = useMutation({ mutationFn: (id) => del(`/calendar/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ["calendar"] }); toast.success("Event deleted"); setDetailEvent(null); } });

  if (isLoading) return <Skeleton className="h-96" />;

  const now = new Date();
  const { year, month } = cursor;
  const first = new Date(year, month, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const eventsFor = (day) => (events || []).filter((e) => {
    const ed = new Date(e.date);
    return ed.getDate() === day && ed.getMonth() === month && ed.getFullYear() === year;
  });

  return (
    <div>
      <PageHeader title="Calendar" subtitle={first.toLocaleDateString("en-US", { month: "long", year: "numeric" })}>
        <div className="flex items-center gap-1 rounded-lg border border-border bg-card p-0.5">
          <button onClick={() => setCursor((c) => c.month === 0 ? { year: c.year - 1, month: 11 } : { ...c, month: c.month - 1 })} className="rounded p-1.5 hover:bg-muted" data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></button>
          <button onClick={() => setCursor({ year: now.getFullYear(), month: now.getMonth() })} className="px-2 text-xs text-muted-foreground hover:text-foreground">Today</button>
          <button onClick={() => setCursor((c) => c.month === 11 ? { year: c.year + 1, month: 0 } : { ...c, month: c.month + 1 })} className="rounded p-1.5 hover:bg-muted" data-testid="cal-next"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <FormDialog title="New Meeting" testid="new-meeting-modal"
          trigger={<Button data-testid="new-meeting-btn"><Plus className="mr-1.5 h-4 w-4" />New Meeting</Button>}
          fields={[
            { name: "title", label: "Title", required: true, full: true },
            { name: "date", label: "Date", type: "date", required: true },
            { name: "start", label: "Start", placeholder: "10:00" },
            { name: "end", label: "End", placeholder: "11:00" },
            { name: "module", label: "Category", type: "select", options: [{ value: "pm", label: "Project" }, { value: "crm", label: "Sales" }, { value: "hrms", label: "HR" }, { value: "company", label: "Company" }] },
            { name: "attendees", label: "Attendees", type: "multiselect", full: true, placeholder: "Select attendees",
              options: users.map((u) => ({ value: u.id, label: u.name })) },
          ]}
          onSubmit={(v) => create.mutateAsync({ ...v, date: new Date(v.date).toISOString(), type: "meeting" })} />
      </PageHeader>

      <div className="mb-3 flex flex-wrap gap-3">
        {LEGEND.map((l) => (
          <span key={l.label} className="flex items-center gap-1.5 text-xs text-muted-foreground"><span className={cn("h-2 w-2 rounded-full", l.cls)} />{l.label}</span>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="grid grid-cols-7 border-b border-border">
          {DAYS.map((d) => <div key={d} className="px-2 py-2 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((day, i) => {
            const evs = day ? eventsFor(day) : [];
            const isToday = day === now.getDate() && month === now.getMonth() && year === now.getFullYear();
            return (
              <div key={i} className={cn("min-h-28 border-b border-r border-border p-1.5", (i + 1) % 7 === 0 && "border-r-0")}>
                {day && (
                  <>
                    <div className={cn("mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs", isToday ? "bg-primary font-semibold text-primary-foreground" : "text-muted-foreground")}>{day}</div>
                    <div className="space-y-1">
                      {evs.map((e) => (["holiday", "leave", "deadline", "company"].includes(e.type)) ? (
                        <button key={e.id} onClick={() => !e.synthetic && setDetailEvent(e)}
                          className={cn("w-full truncate rounded px-1.5 py-0.5 text-left text-[10px] font-medium", TYPE_STYLE[e.type])}>{e.title}</button>
                      ) : (
                        <button key={e.id} data-testid={`event-${e.id}`} onClick={() => setDetailEvent(e)}
                          className="flex w-full items-center gap-1 truncate rounded bg-muted px-1.5 py-0.5 text-left text-[10px] hover:bg-muted/70">
                          <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", MOD_COLOR[e.module])} />
                          <span className="truncate">{e.start} {e.title}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={!!detailEvent} onOpenChange={(v) => !v && setDetailEvent(null)}>
        <DialogContent className="max-w-sm" data-testid="event-detail-modal">
          <DialogHeader><DialogTitle>{detailEvent?.title}</DialogTitle></DialogHeader>
          {detailEvent && (
            <div className="space-y-2 text-sm text-muted-foreground">
              <div>{new Date(detailEvent.date).toLocaleDateString()}{detailEvent.start ? ` · ${detailEvent.start}–${detailEvent.end}` : ""}</div>
              <div className="capitalize">{detailEvent.module} · {detailEvent.type}</div>
              {detailEvent.meet_link && (
                <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2">
                  <Video className="h-4 w-4 shrink-0 text-primary" />
                  <a href={detailEvent.meet_link} target="_blank" rel="noopener noreferrer" className="truncate text-xs text-primary hover:underline">{detailEvent.meet_link}</a>
                  <button type="button" title="Copy link" onClick={() => { navigator.clipboard.writeText(detailEvent.meet_link); toast.success("Meet link copied"); }}
                    className="ml-auto shrink-0 text-muted-foreground hover:text-foreground"><Copy className="h-3.5 w-3.5" /></button>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {can("calendar", "delete") && (
              <ConfirmDelete testid="del-event" title="Delete this event?"
                trigger={<Button variant="outline" className="mr-auto"><Trash2 className="mr-1.5 h-4 w-4" />Delete</Button>}
                onConfirm={() => remove.mutate(detailEvent.id)} />
            )}
            <Button variant="ghost" onClick={() => setDetailEvent(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
